import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { intToBrl } from "../../utils";
import { Amounts, Client, Quote, User } from "../../types";

const QuoteMobile = ({
  quote,
  amounts,
  seller,
  client,
  equipmentMap,
}: {
  quote: Quote;
  amounts: Amounts;
  seller: User;
  client: Client;
  equipmentMap: Record<string, string>;
}) => (
  <div>
    <div className="px-2 py-4 border-b border-gray-400 text-sm flex flex-col gap-2">
      <div className="flex-1 font-bold">
        Proposta comercial n° {quote.number}
      </div>
      <div>
        <span className="font-bold">Consultor:</span>{" "}
        {`${seller.firstName} ${seller.lastName}`}
      </div>
    </div>

    <div className="text-sm px-2 py-4 border-b border-gray-400 flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <div className="font-bold">Cliente</div>
        <div>{client.name}</div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="font-bold">Endereço</div>
        <div>
          {client.street}, {client.number}, {client.area}, {client.city},{" "}
          {client.state} - {client.zipcode}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="font-bold">CPF/CNPJ</div>
        <div>{client.document}</div>
      </div>
    </div>

    {quote.itemsSold.length > 0 && (
      <div className="px-2 py-4 border-b border-gray-400">
        <div className="font-bold mb-4">Produtos adquiridos</div>

        <div className="flex flex-col gap-2">
          {quote.itemsSold.map((item, i) => (
            <div key={i} className="border rounded">
              <div className="p-2 text-sm whitespace-nowrap overflow-scroll no-scrollbar border-b border-dashed">
                {equipmentMap[item.equipmentUuid]}
              </div>

              <div className="flex text-xs p-2">
                <div className="flex-1 text-gray-500">
                  <span className="font-semibold">{item.amount} × </span>
                  <span>{intToBrl(item.price)}</span>
                </div>
                <div>
                  <span className="font-bold">
                    {intToBrl(item.price * item.amount)}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {quote.discountSale > 0 && (
            <div className="border rounded p-2 text-sm flex mt-4">
              <div className="flex-1 font-semibold">Desconto</div>
              <div className="font-bold">— {intToBrl(quote.discountSale)}</div>
            </div>
          )}

          <div className="flex px-2 py-4">
            <div className="flex-1 font-semibold">Valor final</div>
            <div className="font-extrabold">{intToBrl(amounts.finalSale)}</div>
          </div>
        </div>
      </div>
    )}

    {(quote.itemsRented.length > 0 || quote.feeMonitoring > 0) && (
      <div className="px-2 py-4 border-b border-gray-400">
        <div className="font-bold mb-4">Equipamentos locados</div>

        <div className="flex flex-col gap-2">
          {quote.itemsRented.map((item, i) => (
            <div key={i} className="border rounded">
              <div className="p-2 text-sm whitespace-nowrap overflow-scroll no-scrollbar border-b border-dashed">
                {equipmentMap[item.equipmentUuid]}
              </div>

              <div className="flex text-xs p-2">
                <div className="flex-1 text-gray-500">
                  <span className="font-semibold">{item.amount} × </span>
                  <span>{intToBrl(item.price)}</span>
                </div>
                <div>
                  <span className="font-bold">
                    {intToBrl(item.price * item.amount)}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {quote.feeMonitoring > 0 && (
            <div className="border rounded">
              <div className="p-2 text-sm border-b border-dashed font-semibold">
                Monitoramento
              </div>

              <div className="flex text-xs p-2">
                <div className="flex-1 text-gray-500"></div>
                <div>
                  <span className="font-bold">
                    {intToBrl(quote.feeMonitoring)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {quote.discountRent > 0 && (
            <div className="border rounded p-2 text-sm flex mt-4">
              <div className="flex-1 font-semibold">Desconto</div>
              <div className="font-bold">— {intToBrl(quote.discountRent)}</div>
            </div>
          )}

          <div className="flex px-2 py-4">
            <div className="flex-1 font-semibold">Mensalidade</div>
            <div className="font-extrabold">{intToBrl(amounts.finalRent)}</div>
          </div>
        </div>
      </div>
    )}

    {(quote.feeInstall > 0 || quote.feeReview > 0) && (
      <div className="px-2 py-4 border-b border-gray-400">
        <div className="font-bold mb-4">Encargos e taxas</div>

        {quote.feeInstall > 0 && (
          <div className="flex px-2 text-sm">
            <div className="flex-1">Taxa de instalação</div>
            <div className="font-bold">{intToBrl(quote.feeInstall)}</div>
          </div>
        )}

        {quote.feeReview > 0 && (
          <div className="flex px-2 text-sm">
            <div className="flex-1">Taxa de revisão</div>
            <div className="font-bold">{intToBrl(quote.feeReview)}</div>
          </div>
        )}
      </div>
    )}

    <div>
      <div className="bg-gray-100 p-2 font-bold text-center">Resumo</div>

      <div className="py-4 px-2 flex flex-col gap-4">
        {amounts.finalRent > 0 && (
          <div>
            <div className="text-sm font-semibold">Mensalidades</div>
            <div className="font-bold">{intToBrl(amounts.finalRent)}</div>
          </div>
        )}

        {quote.feeInstall > 0 && (
          <div>
            <div className="text-sm font-semibold">Taxa de instalação</div>
            <div className="">
              <span className="font-bold">{intToBrl(quote.feeInstall)} </span>
              <span className="text-xs text-gray-600">
                a ser pago antes do início do serviço
              </span>
            </div>
          </div>
        )}

        {quote.feeReview > 0 && (
          <div>
            <div className="text-sm font-semibold">Taxa de revisão</div>
            <div className="">
              <span className="font-bold">{intToBrl(quote.feeReview)} </span>
              <span className="text-xs text-gray-600">
                a ser pago antes do início do serviço
              </span>
            </div>
          </div>
        )}

        {amounts.finalSale > 0 && (
          <div>
            <div className="text-sm font-semibold">Produtos adquiridos</div>
            <div className="text-xs text-gray-600">
              {quote.maxInstallments <= 1 && (
                <>
                  Pagamento em até 30 dias:{" "}
                  <span className="font-bold text-base text-gray-900">
                    {intToBrl(amounts.finalSale)}
                  </span>
                </>
              )}

              {quote.maxInstallments === 2 && (
                <>
                  Entrada de{" "}
                  <span className="font-bold text-base text-gray-900">
                    {intToBrl(Math.round(amounts.finalSale / 2))}
                  </span>{" "}
                  e uma parcela de{" "}
                  <span className="font-bold text-base text-gray-900">
                    {intToBrl(Math.round(amounts.finalSale / 2))}
                  </span>
                </>
              )}

              {quote.maxInstallments >= 3 && (
                <>
                  Entrada de{" "}
                  <span className="font-bold text-base text-gray-900">
                    {intToBrl(
                      Math.round(amounts.finalSale / quote.maxInstallments)
                    )}
                  </span>{" "}
                  + {quote.maxInstallments - 1} parcelas de{" "}
                  <span className="font-bold text-base text-gray-900">
                    {intToBrl(
                      Math.round(amounts.finalSale / quote.maxInstallments)
                    )}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {amounts.finalSale > 0 && (
          <div>
            <div className="text-sm font-semibold">Pagamento à vista</div>
            <div className="">
              <span className="font-bold">
                {intToBrl(Math.round(amounts.finalSale * 0.94))}
              </span>
              <span className="text-xs text-gray-600 ml-1">
                (6% de desconto para produtos adquiridos)
              </span>
            </div>
          </div>
        )}

        {quote.note && (
          <div>
            <div className="text-sm font-semibold">Observações</div>
            <div className="italic text-sm text-gray-600">{quote.note}</div>
          </div>
        )}
      </div>
    </div>

    <div className="border-2 m-2 text-sm">
      <div className="bg-gray-200 font-semibold text-center text-xs p-1">
        O consultor não está autorizado a receber nenhum valor — Dúvidas, ligue:
        27 3232-1000
      </div>
    </div>

    <div className="text-xs text-gray-500 leading-3 p-2 mb-4">
      Não inclui serviços de alvenaria, tubulação, solda ou elétrica.
      <br />
      Modelo do contrato disponível no site alarmsystem.com.br
      <br />{" "}
      <span className="font-semibold">
        Autorize essa proposta com o link recebido por email
      </span>
    </div>

    <div className="text-center py-5 mb-16">
      <a
        href="https://www.instagram.com/alarmsystem.es/"
        target="_blank"
        rel="noreferrer"
        className="text-2xl px-2"
      >
        <FontAwesomeIcon icon={faInstagram} />
      </a>

      <a
        href="https://wa.me/5527999481419"
        target="_blank"
        rel="noreferrer"
        className="text-2xl px-2"
      >
        <FontAwesomeIcon icon={faWhatsapp} />
      </a>
    </div>
  </div>
);

export default QuoteMobile;
